import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ErrorTranslation,
  ErrorTranslationConfig,
  TranslatedError,
} from '../models/error-translation.model';
import { ERROR_TRANSLATION_CONFIG } from '../tokens/error-config.token';
import { BASE_ERROR_TRANSLATIONS } from '../translations/base-error.translations';

/**
 * Service that translates HTTP errors into user-friendly messages.
 * Supports a three-layer architecture: base, domain, and app-specific translations.
 */
@Injectable({
  providedIn: 'root',
})
export class ErrorTranslatorService {
  private translations: Map<string, ErrorTranslation> = new Map();
  private matchers: ErrorTranslation[] = [];
  private enableContextDetection = false;
  private defaultMessage = 'An unexpected error occurred. Please try again.';

  private readonly errorConfig:
    | ErrorTranslationConfig[]
    | ErrorTranslationConfig
    | null = inject(ERROR_TRANSLATION_CONFIG, { optional: true });

  constructor() {
    const configs = this.errorConfig;
    // Initialize with base translations
    this.registerTranslations(BASE_ERROR_TRANSLATIONS);

    // Apply configurations in order (allowing overrides)
    if (configs) {
      const configArray = Array.isArray(configs) ? configs : [configs];

      configArray.forEach(config => {
        if (config.translations) {
          this.registerTranslations(config.translations);
        }

        if (config.enableContextDetection !== undefined) {
          this.enableContextDetection = config.enableContextDetection;
        }

        if (config.defaultMessage) {
          this.defaultMessage = config.defaultMessage;
        }
      });
    }

    // Sort matchers by priority (higher first)
    this.matchers.sort((a, b) => (b.priority || 0) - (a.priority || 0));
  }

  /**
   * Register translations, overriding existing ones with the same code
   */
  private registerTranslations(translations: ErrorTranslation[]): void {
    translations.forEach(translation => {
      if (translation.code) {
        this.translations.set(translation.code, translation);
      }

      if (translation.matcher) {
        this.matchers.push(translation);
      }
    });
  }

  /**
   * Translate an HTTP error into a user-friendly message
   */
  translateError(error: HttpErrorResponse): TranslatedError {
    let translation: ErrorTranslation | undefined;
    let matchedCode: string | undefined;

    // First, try to find a translation by error code if present in the error response
    if (error.error?.code) {
      translation = this.translations.get(error.error.code);
      matchedCode = error.error.code;
    }

    // If no code match, try context-aware matching
    if (!translation && this.enableContextDetection) {
      translation = this.findContextualTranslation(error);
      matchedCode = translation?.code;
    }

    // If still no match, use matchers
    if (!translation) {
      translation = this.findTranslationByMatcher(error);
      matchedCode = translation?.code;
    }

    // Extract message from error response if available
    const message =
      translation?.message ||
      this.extractErrorMessage(error) ||
      this.defaultMessage;
    const title = translation?.title || this.getDefaultTitle(error.status);

    return {
      message,
      title,
      originalError: error,
      matchedCode,
      isFallback: !translation || translation.code === 'common.generic.error',
    };
  }

  /**
   * Find a translation using matcher functions
   */
  private findTranslationByMatcher(
    error: HttpErrorResponse,
  ): ErrorTranslation | undefined {
    return this.matchers.find(t => t.matcher && t.matcher(error));
  }

  /**
   * Find a translation based on context (URL, method, etc.)
   */
  private findContextualTranslation(
    error: HttpErrorResponse,
  ): ErrorTranslation | undefined {
    if (!error.url) return undefined;

    // Check all translations with context
    const contextualTranslations = Array.from(this.translations.values())
      .filter(t => t.context)
      .sort((a, b) => (b.priority || 0) - (a.priority || 0));

    for (const translation of contextualTranslations) {
      const context = translation.context!;

      // Check URL pattern
      if (context.urlPattern && !context.urlPattern.test(error.url)) {
        continue;
      }

      // Check HTTP methods
      if (
        context.methods &&
        !context.methods.includes(error.headers?.get('method') || '')
      ) {
        continue;
      }

      // All context conditions match
      return translation;
    }

    return undefined;
  }

  /**
   * Extract error message from the error response
   * Handles nested structures like { error: { message: "..." } }
   */
  private extractErrorMessage(error: HttpErrorResponse): string | undefined {
    // Try to get message from error body
    if (error.error) {
      if (typeof error.error === 'string') {
        return error.error;
      }

      // Handle nested error structure: { error: { message: "..." } }
      if (error.error.error?.message) {
        return error.error.error.message;
      }

      // Handle flat structure: { message: "..." }
      if (error.error.message) {
        return error.error.message;
      }

      // Handle nested error as string: { error: "message" }
      if (typeof error.error.error === 'string') {
        return error.error.error;
      }

      if (error.error.errors && Array.isArray(error.error.errors)) {
        return error.error.errors.join(', ');
      }
    }

    // Use status text if available and meaningful
    if (
      error.statusText &&
      error.statusText !== 'Unknown Error' &&
      error.statusText !== 'OK'
    ) {
      return error.statusText;
    }

    return undefined;
  }

  /**
   * Get default title based on HTTP status code
   */
  private getDefaultTitle(status: number): string {
    if (status === 0) {
      return 'Connection Error';
    } else if (status >= 500) {
      return 'Server Error';
    } else if (status === 404) {
      return 'Not Found';
    } else if (status === 403) {
      return 'Access Denied';
    } else if (status === 401) {
      return 'Unauthorized';
    } else if (status >= 400) {
      return 'Request Error';
    }
    return 'Error';
  }

  /**
   * Add or update a translation at runtime
   */
  addTranslation(translation: ErrorTranslation): void {
    if (translation.code) {
      this.translations.set(translation.code, translation);
    }

    if (translation.matcher) {
      // Remove existing matcher with same code if present
      if (translation.code) {
        this.matchers = this.matchers.filter(t => t.code !== translation.code);
      }

      this.matchers.push(translation);
      this.matchers.sort((a, b) => (b.priority || 0) - (a.priority || 0));
    }
  }

  /**
   * Get all registered translations (for debugging)
   */
  getTranslations(): ErrorTranslation[] {
    return Array.from(this.translations.values());
  }
}
